import { motion } from 'framer-motion'
import { Download, MapPin, Calendar, Award, Code2, Database, Palette, Globe, ExternalLink, Github, Mail, Info } from 'lucide-react'

const About = () => {
  const skills = [
    {
      category: 'Frontend',
      icon: Code2,
      items: ['JavaScript', 'HTML', 'CSS', 'React', 'TailwindCSS', 'Bootstrap', 'Sass']
    },
    {
      category: 'Backend',
      icon: Database,
      items: ['Node.js', 'PHP', 'Laravel', 'MySQL', 'API Development']
    },
    {
      category: 'Design',
      icon: Palette,
      items: ['UI/UX Design', 'Responsive Design']
    },
    {
      category: 'Tools & Others',
      icon: Globe,
      items: ['Git', 'Docker', 'Vite', 'Sakura Internet', 'Vercel', 'Netlify', 'Figma', 'Canva', 'Xmind', 'AI']
    }
  ]

  const learningJourney = [
    {
      title: '最新技術の習得',
      period: '2025年',
      description: 'AIツールを活用したコーディング（Vibe Coding）等、最新技術を積極的に学習中。',
      icon: '🚀',
      isCurrent: true
    },
    {
      title: '開発環境・フレームワークの導入',
      period: '2024年',
      description: 'Dockerによる開発環境構築や、Laravelを中心としたフレームワークの学習を開始。',
      icon: '⚙️'
    },
    {
      title: 'バージョン管理の習得',
      period: '2023年',
      description: 'Git, GitHubを使用したバージョン管理を学び、チーム開発の基礎を学ぶ。',
      icon: '📚'
    },
    {
      title: 'Web開発の学習開始',
      period: '2022年9月',
      description: 'HTML, CSS, PHPの学習を開始。基本的なWeb開発の基礎を学ぶ。',
      icon: '🌱'
    }
  ]

  return (
    <div className="min-h-screen pt-16">
      {/* Header Section */}
{/* 自己紹介セクション */}
<section className="relative py-16 px-4 sm:px-6 lg:px-8">

<motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="gradient-text">Me</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Passionate frontend engineer with a love for creating beautiful, 
              functional web experiences that make a difference.
            </p>
          </motion.div>

  <div className="max-w-6xl mx-auto">
    <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
      {/* プロフィール写真 */}
      <div className="w-full lg:w-2/5 flex justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative group"
        >
          <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-blue-400/30 group-hover:border-blue-400/60 transition-all duration-300 shadow-2xl">
            <img
              src="/images/projects/profile-1.png"
              alt="プロフィール写真"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity p-4">
              <span className="text-white font-medium text-sm sm:text-base md:text-lg text-center">
                初めまして、江口と申します。
                <br />
                皆からはテンやテンさんと呼ばれています。
                <br />
                <br />
                どうぞごゆっくり、サイトをご覧ください！
              </span>
            </div>
          </div>
          <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-48 h-1.5 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full blur-sm opacity-70 group-hover:opacity-100 transition-opacity"></div>
        </motion.div>
      </div>
      
      {/* 自己紹介テキスト */}
      <div className="w-full lg:w-3/5 text-center lg:text-left">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            江口　天
          </h1>
          <p className="text-xl md:text-2xl text-blue-300 mb-6">
            Eguchi　　 Takashi 
            {/* <br />
             Web Developer & Designer */}
          </p>
          <div className="max-w-2xl mx-auto lg:mx-0">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              ユーザーエクスペリエンスを第一に考えた、美しく使いやすいWebアプリケーションを開発しています。
              最新の技術を学び続け、常にスキルアップを心がけています。
            </p>
            <p className="text-gray-400 mb-8">
              #Web開発  #フロントエンド #バックエンド #フルスタック #バイブコーディング
            </p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-4">
              <div className="relative group">
                <motion.a
                  href="mailto:takashi.eguchi.dev@gmail.com"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-blue-500/20 transition-all text-sm sm:text-base inline-flex items-center"
                >
                  <Mail size={16} className="mr-1.5" />
                  Get In Touch
                </motion.a>
                <div className="absolute -top-9 left-1/2 transform -translate-x-1/2 px-2.5 py-1 bg-gray-800 text-white text-xs rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg border border-gray-700">
                  takashi.eguchi.dev@gmail.com
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-gray-800 rotate-45 border-b border-r border-gray-700"></div>
                </div>
              </div>
              
              <motion.a
                href="https://github.com/Ten10sun"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-2.5 bg-blue-600/20 text-white font-semibold rounded-full hover:bg-blue-600/30 transition-all text-sm sm:text-base inline-flex items-center "
              >
                <Github size={16} className="mr-1.5" />
                View GitHub
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </div>
</section>

      {/* Skills Section */}
      <section className="py-20 section-padding bg-black/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Skills & Technologies
            </h2>
            <p className="text-xl text-gray-300">
              Technologies and tools I work with
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {skills.map((skillGroup, index) => {
              const Icon = skillGroup.icon
              return (
                <motion.div
                  key={skillGroup.category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{skillGroup.category}</h3>
                  <div className="space-y-2">
                    {skillGroup.items.map((skill) => (
                      <div
                        key={skill}
                        className="px-3 py-1 bg-white/10 text-gray-300 text-sm rounded-full inline-block mr-2 mb-2"
                      >
                        {skill}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Learning Journey Section */}
      <section className="py-20 section-padding">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Learning Journey
            </h2>
            <p className="text-xl text-gray-300">
              My development journey
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 h-full w-0.5 bg-gradient-to-b from-blue-400/30 via-purple-400/30 to-pink-400/30"></div>
            
            <div className="space-y-12">
              {learningJourney.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-6`}
                >
                  {/* Timeline dot */}
                  <div className={`absolute left-0 md:left-1/2 w-8 h-8 rounded-full flex items-center justify-center text-xl z-10 -ml-4 ${item.isCurrent ? 'bg-gradient-to-br from-blue-400 to-purple-500' : 'bg-blue-500/20'} border-2 border-white/10`}>
                    {item.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="ml-12 md:ml-0 md:w-5/12">
                    <div className={`bg-gray-800/70 backdrop-blur-sm rounded-xl p-6 border ${item.isCurrent ? 'border-blue-400/50' : 'border-gray-700/50'} hover:border-blue-400/30 transition-all`}>
                      <div className="flex flex-col gap-2">
                        <h3 className="text-xl font-bold text-white">{item.title}</h3>
                        <span className="text-sm px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full w-fit">
                          {item.period}
                        </span>
                        <p className="mt-2 text-gray-300">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Let's Work Together
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              I'm always interested in new opportunities and exciting projects.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="relative group">
                <motion.a
                  href="mailto:takashi.eguchi.dev@gmail.com"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-2xl transition-all duration-300 inline-flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Get In Touch
                </motion.a>
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-800 text-white text-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg border border-gray-700">
                  takashi.eguchi.dev@gmail.com
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45 border-b border-r border-gray-700"></div>
                </div>
              </div>
              <motion.a
                href="/projects"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gray-700/50 hover:bg-gray-600/50 text-white font-semibold rounded-full border border-gray-600 transition-all duration-300 inline-flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                View Projects
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About
